import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useAuth } from '../../hooks/useAuth';
import { userDisplayName, userInitials } from '../../types';
import { ThemeToggle } from '../ui/ThemeToggle';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { MessagesBell } from '../ui/MessagesBell';
import { NotificationBell } from '../ui/NotificationBell';

const DRAWER_WIDTH = 248;

export function AppShell() {
  const { t } = useTranslation();
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('ADMIN');
  const isProvider = hasRole('PROVIDER');

  const items = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: <DashboardIcon /> },
    { to: '/search', label: t('nav.discover'), icon: <SearchIcon /> },
    { to: '/opportunities', label: t('nav.opportunities'), icon: <LightbulbOutlinedIcon /> },
    { to: '/bookings', label: t('nav.myBookings'), icon: <EventAvailableRoundedIcon /> },
    { to: '/messages', label: t('nav.messages'), icon: <ChatBubbleOutlineRoundedIcon /> },
    { to: '/my-requests', label: t('nav.myRequests'), icon: <InboxRoundedIcon /> },
    ...(isProvider
      ? [
          {
            to: '/dashboard/bookings',
            label: t('nav.bookingRequests'),
            icon: <InboxRoundedIcon />,
          },
          {
            to: '/dashboard/availability',
            label: t('nav.myCalendar'),
            icon: <CalendarMonthRoundedIcon />,
          },
        ]
      : [
          {
            to: '/become-provider',
            label: t('nav.becomeProvider'),
            icon: <StorefrontIcon />,
          },
        ]),
    ...(isAdmin
      ? [
          { to: '/admin/providers', label: t('nav.admin'), icon: <AdminPanelSettingsIcon /> },
          { to: '/admin/users', label: t('nav.adminUsers'), icon: <GroupsRoundedIcon /> },
          { to: '/admin/insights', label: t('nav.adminInsights'), icon: <TrendingUpRoundedIcon /> },
        ]
      : []),
  ];

  const handleLogout = async () => {
    setAnchor(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = user ? userInitials(user) : '?';
  const displayName = user ? userDisplayName(user) : '';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2 }}>
        {/* Horizontal wordmark (A + "Afro Espace" inline). */}
        <Box
          component="img"
          src="/logo-color.png"
          alt={t('app.name')}
          loading="eager"
          sx={{
            display: 'block',
            height: 36,
            width: 'auto',
            maxWidth: 150,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, py: 1 }}>
        {items.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            sx={{
              mx: 1,
              borderRadius: 1.5,
              '&.active': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen((o) => !o)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          <LanguageSwitcher />
          <ThemeToggle />
          <MessagesBell />
          <NotificationBell />
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
              {initials}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchor(null);
                navigate('/account');
              }}
            >
              <ListItemIcon>
                <PersonOutlineIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.account')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          // minWidth: 0 lets flex children that contain wide content
          // (e.g. long French labels) shrink to fit the viewport instead of
          // forcing horizontal scroll on small screens.
          minWidth: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
